import React, { useState, useEffect } from 'react';
import Icon from '../../../AppIcon';

const StatsSection = () => {
  const [counters, setCounters] = useState({
    matches: 0,
    revenue: 0,
    startups: 0,
    success: 0
  });

  const finalStats = {
    matches: 47832,
    revenue: 2.8,
    startups: 15247,
    success: 94.2
  };

  const statsData = [
    {
      id: 'matches',
      label: 'Successful Matches',
      value: counters?.matches,
      suffix: '+',
      icon: 'Target',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      description: 'Corporate-startup partnerships created'
    },
    {
      id: 'revenue',
      label: 'Revenue Generated',
      value: counters?.revenue,
      prefix: '$',
      suffix: 'B+',
      icon: 'TrendingUp',
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
      description: 'Total value created through platform'
    },
    {
      id: 'startups',
      label: 'Verified Startups',
      value: counters?.startups,
      suffix: '+',
      icon: 'Rocket',
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      description: 'Quality-verified startup profiles'
    },
    {
      id: 'success',
      label: 'Success Rate',
      value: counters?.success,
      suffix: '%',
      icon: 'Award',
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      description: 'Partnership conversion rate'
    }
  ];

  useEffect(() => {
    const duration = 2000;
    const steps = 60;
    const stepDuration = duration / steps;

    const timer = setInterval(() => {
      setCounters(prev => ({
        matches: Math.min(prev?.matches + Math.ceil(finalStats?.matches / steps), finalStats?.matches),
        revenue: Math.min(prev?.revenue + (finalStats?.revenue / steps), finalStats?.revenue),
        startups: Math.min(prev?.startups + Math.ceil(finalStats?.startups / steps), finalStats?.startups),
        success: Math.min(prev?.success + (finalStats?.success / steps), finalStats?.success)
      }));
    }, stepDuration);

    setTimeout(() => {
      clearInterval(timer);
      setCounters(finalStats);
    }, duration);

    return () => clearInterval(timer);
  }, []);

  const formatNumber = (num, decimals = 0) => {
    if (decimals > 0) {
      return num?.toFixed(decimals);
    }
    return Math.floor(num)?.toLocaleString();
  };

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Icon name="BarChart3" size={16} />
            <span>Platform Performance</span>
          </div>
          <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
            Driving Innovation at Scale
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Real-time metrics showcasing the impact of our intelligent matching ecosystem across the global innovation landscape.
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {statsData?.map((stat) => (
            <div
              key={stat?.id}
              className="relative bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-slate-100 group"
            >
              {/* Icon */}
              <div className={`w-12 h-12 ${stat?.bgColor} rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-200`}>
                <Icon name={stat?.icon} size={24} className={stat?.color} />
              </div>

              {/* Value */}
              <div className="mb-2">
                <div className="flex items-baseline space-x-1">
                  {stat?.prefix && (
                    <span className="text-2xl font-bold text-slate-900">{stat?.prefix}</span>
                  )}
                  <span className="text-3xl lg:text-4xl font-bold text-slate-900 animate-counter">
                    {formatNumber(stat?.value, stat?.id === 'revenue' ? 1 : stat?.id === 'success' ? 1 : 0)}
                  </span>
                  {stat?.suffix && (
                    <span className="text-2xl font-bold text-slate-900">{stat?.suffix}</span>
                  )}
                </div>
              </div>

              {/* Label */}
              <h3 className="text-lg font-semibold text-slate-900 mb-1">
                {stat?.label}
              </h3>

              {/* Description */}
              <p className="text-sm text-slate-600">
                {stat?.description}
              </p>

              {/* Trend Indicator */}
              <div className="absolute top-4 right-4">
                <div className="flex items-center space-x-1 text-emerald-600">
                  <Icon name="TrendingUp" size={14} />
                  <span className="text-xs font-medium">+12%</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Additional Metrics */}
        <div className="bg-gradient-to-r from-slate-50 to-blue-50 rounded-2xl p-8">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Globe" size={28} className="text-blue-600" />
              </div>
              <h4 className="text-2xl font-bold text-slate-900 mb-2">85+ Countries</h4>
              <p className="text-slate-600">Global innovation network coverage</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Clock" size={28} className="text-emerald-600" />
              </div>
              <h4 className="text-2xl font-bold text-slate-900 mb-2">24/7 Matching</h4>
              <p className="text-slate-600">Real-time opportunity identification</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Zap" size={28} className="text-purple-600" />
              </div>
              <h4 className="text-2xl font-bold text-slate-900 mb-2">&lt;48 Hours</h4>
              <p className="text-slate-600">Average matching response time</p>
            </div>
          </div>
        </div>

        {/* Live Activity Feed */}
        <div className="mt-12 bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-slate-900">Live Platform Activity</h3>
            <div className="flex items-center space-x-2 text-emerald-600">
              <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
              <span className="text-sm font-medium">Live Updates</span>
            </div>
          </div>

          <div className="space-y-3">
            {[
              { action: 'New match created', company: 'TechCorp Inc.', startup: 'AI Solutions Ltd.', time: '2 min ago' },
              { action: 'Referral completed', intermediary: 'Innovation Hub Berlin', amount: '$15,000', time: '5 min ago' },
              { action: 'Startup verified', startup: 'GreenTech Innovations', location: 'San Francisco', time: '8 min ago' },
              { action: 'Program launched', company: 'Global Ventures', budget: '$2M', time: '12 min ago' }
            ]?.map((activity, index) => (
              <div key={index} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-b-0">
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <span className="text-sm text-slate-700">{activity?.action}</span>
                  <span className="text-sm font-medium text-slate-900">
                    {activity?.company || activity?.intermediary || activity?.startup}
                  </span>
                  {activity?.startup && activity?.company && (
                    <span className="text-sm text-slate-600">→ {activity?.startup}</span>
                  )}
                </div>
                <span className="text-xs text-slate-500">{activity?.time}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default StatsSection;